import { config } from '../config.ts';
import { signRequest } from './auth.ts';
import { getOnChainVerifyingContract } from './wallet/eip712.ts';
import type { AuthContext, MarginMode } from '../types/index.ts';

export type OrderlyChainType = 'EVM' | 'SOL';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function pushLog(_entry: Record<string, unknown>) { /* no-op */ }
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function updateLastLog(_patch: Record<string, unknown>) { /* no-op */ }

const BASE = config.apiBaseUrl;

// ── Chain ID → Display Name ──
export const CHAIN_NAMES: Record<string, string> = {
  '1': 'Ethereum',
  '10': 'Optimism',
  '56': 'BNB Smart Chain',
  '137': 'Polygon',
  '143': 'Monad',
  '146': 'Sonic',
  '1329': 'Sei',
  '1514': 'Story',
  '2741': 'Abstract',
  '2818': 'Morph',
  '5000': 'Mantle',
  '8453': 'Base',
  '34443': 'Mode',
  '42161': 'Arbitrum',
  '43114': 'Avalanche',
  '80094': 'Berachain',
  '98866': 'Plume',
  '900900900': 'Solana',
};

// ── Core Fetch Wrappers ──

function ts() { return new Date().toLocaleTimeString(); }

function unwrap<T>(json: { success: boolean; data?: T; message?: string; code?: number }): T {
  if (json.success === false) throw new Error(json.message ?? `API error ${json.code}`);
  return (json.data ?? json) as T;
}

async function publicGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${BASE}${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  pushLog({ time: ts(), method: 'GET', path, status: 'pending' });
  const res = await fetch(url.toString());
  if (!res.ok) {
    const text = await res.text();
    updateLastLog({ status: res.status, response: text });
    throw new Error(`API ${res.status}: ${text}`);
  }
  const json = await res.json();
  updateLastLog({ status: res.status, response: JSON.stringify(json).slice(0, 200) });
  return unwrap<T>(json);
}

async function signedGet<T>(path: string, auth: AuthContext, params?: Record<string, string>, accountIdOverride?: string): Promise<T> {
  const url = new URL(`${BASE}${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const signPath = params ? `${path}?${url.searchParams.toString()}` : path;
  const { timestamp, signature } = signRequest('GET', signPath, null, auth.secretKey);
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
    'orderly-timestamp': String(timestamp),
    'orderly-account-id': accountIdOverride ?? auth.accountId,
    'orderly-key': auth.orderlyKey,
    'orderly-signature': signature,
  };
  pushLog({ time: ts(), method: 'GET', path, status: 'pending', headers });
  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    const text = await res.text();
    updateLastLog({ status: res.status, response: text });
    throw new Error(`API ${res.status}: ${text}`);
  }
  const json = await res.json();
  updateLastLog({ status: res.status, response: JSON.stringify(json).slice(0, 200) });
  return unwrap<T>(json);
}

async function signedDelete<T>(path: string, auth: AuthContext, params?: Record<string, string>, accountIdOverride?: string): Promise<T> {
  const url = new URL(`${BASE}${path}`);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const signPath = params ? `${path}?${url.searchParams.toString()}` : path;
  const { timestamp, signature } = signRequest('DELETE', signPath, null, auth.secretKey);
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
    'orderly-timestamp': String(timestamp),
    'orderly-account-id': accountIdOverride ?? auth.accountId,
    'orderly-key': auth.orderlyKey,
    'orderly-signature': signature,
  };
  pushLog({ time: ts(), method: 'DELETE', path, status: 'pending', headers });
  const res = await fetch(url.toString(), { method: 'DELETE', headers });
  if (!res.ok) {
    const text = await res.text();
    updateLastLog({ status: res.status, response: text });
    throw new Error(`API ${res.status}: ${text}`);
  }
  const json = await res.json();
  updateLastLog({ status: res.status, response: JSON.stringify(json).slice(0, 200) });
  return unwrap<T>(json);
}

async function signedPost<T>(path: string, body: Record<string, unknown>, auth: AuthContext, accountIdOverride?: string): Promise<T> {
  const bodyStr = JSON.stringify(body);
  const { timestamp, signature } = signRequest('POST', path, bodyStr, auth.secretKey);
  const headers = {
    'Content-Type': 'application/json',
    'orderly-timestamp': String(timestamp),
    'orderly-account-id': accountIdOverride ?? auth.accountId,
    'orderly-key': auth.orderlyKey,
    'orderly-signature': signature,
  };
  pushLog({ time: ts(), method: 'POST', path, status: 'pending', headers, body: bodyStr });
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: bodyStr });
  if (!res.ok) {
    const text = await res.text();
    updateLastLog({ status: res.status, response: text });
    throw new Error(`API ${res.status}: ${text}`);
  }
  const json = await res.json();
  updateLastLog({ status: res.status, response: JSON.stringify(json).slice(0, 200) });
  return unwrap<T>(json);
}

// ── Public Endpoints ──

export async function getBrokers(address: string, chainType: OrderlyChainType): Promise<{ broker_id: string; broker_name: string }[]> {
  // 1. Get broker IDs the user is registered with
  const registered = await publicGet<{ broker_id: string[] }>('/v1/get_broker', { address, chain_type: chainType });
  const ids: string[] = [...new Set(registered.broker_id ?? [])];
  if (ids.length === 0) return [];

  // 2. Get all broker names for display
  const allBrokers = await publicGet<{ rows: { broker_id: string; broker_name: string }[] }>('/v1/public/broker/name');
  const nameMap = new Map((allBrokers.rows ?? []).map((b) => [b.broker_id, b.broker_name]));

  // 3. Return all registered brokers for this wallet
  return ids.map((id) => ({ broker_id: id, broker_name: nameMap.get(id) ?? id }));
}

export async function getAccount(address: string, brokerId: string, chainType?: OrderlyChainType): Promise<{ account_id: string; user_id: number } | null> {
  try {
    return await publicGet('/v1/get_account', { address, broker_id: brokerId, ...(chainType ? { chain_type: chainType } : {}) });
  } catch {
    return null;
  }
}

export interface OrderlyAccountSummary {
  account_id: string;
  user_id: number;
  user_type?: string;
}

export async function getAllAccounts(address: string, brokerId: string): Promise<OrderlyAccountSummary[]> {
  const result = await publicGet<{ rows: OrderlyAccountSummary[] }>('/v1/get_all_accounts', { address, broker_id: brokerId });
  return result.rows ?? [];
}

export async function getRegistrationNonce(): Promise<{ registration_nonce: string }> {
  return publicGet('/v1/registration_nonce');
}

export async function getFuturesInfo(): Promise<{
  rows: { symbol: string; index_price: number; mark_price: number }[]
}> {
  return publicGet('/v1/public/futures');
}

export async function getFundingRates(): Promise<{
  rows: { symbol: string; sum_unitary_funding: number; last_funding_rate: number }[]
}> {
  return publicGet('/v1/public/funding_rates');
}

export interface TokenChainDetail {
  chain_id: string;
  contract_address: string;
  decimals: number;
  withdrawal_fee: number;
  cross_chain_withdrawal_fee?: number;
  display_name: string;
}

export interface TokenInfo {
  token: string;
  decimals: number;
  minimum_withdraw_amount: number;
  base_weight?: number;
  discount_factor?: number;
  user_max_qty?: number;
  chain_details: TokenChainDetail[];
}

export async function getTokenInfo(): Promise<{ rows: TokenInfo[] }> {
  return publicGet('/v1/public/token');
}

// ── EIP-712 Signed Endpoints (signature in body, no Ed25519 headers) ──

async function eip712Post<T>(path: string, body: Record<string, unknown>, accountId?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accountId) headers['orderly-account-id'] = accountId;
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return unwrap<T>(await res.json());
}

export async function registerAccount(payload: {
  message: { brokerId: string; chainId: number; timestamp: number; registrationNonce: string };
  signature: string;
  userAddress: string;
}): Promise<{ account_id: string; orderly_account_id: string }> {
  return eip712Post('/v1/register_account', payload);
}

export async function addOrderlyKey(payload: {
  message: { brokerId: string; chainId: number; orderlyKey: string; scope: string; timestamp: number; expiration: number; chainType?: OrderlyChainType; tag?: string };
  signature: string;
  userAddress: string;
}): Promise<{ orderly_key: string }> {
  return eip712Post('/v1/orderly_key', payload);
}

export async function settlePnl(payload: {
  message: { brokerId: string; chainId: number; settleNonce: number; timestamp: number; chainType?: OrderlyChainType };
  signature: string;
  userAddress: string;
}, auth: AuthContext): Promise<unknown> {
  return signedPost('/v1/settle_pnl', {
    message: { ...payload.message, chainType: payload.message.chainType ?? 'EVM' },
    signature: payload.signature,
    userAddress: payload.userAddress,
    verifyingContract: getOnChainVerifyingContract(),
  }, auth);
}

export async function withdrawRequest(payload: {
  message: { brokerId: string; chainId: number; receiver: string; token: string; amount: string; withdrawNonce: number; timestamp: number; allowCrossChainWithdraw: false; chainType?: OrderlyChainType };
  signature: string;
  userAddress: string;
}, auth: AuthContext): Promise<{ withdraw_id: number | string }> {
  const body = {
    message: { ...payload.message, chainType: payload.message.chainType ?? 'EVM' },
    signature: payload.signature,
    userAddress: payload.userAddress,
    verifyingContract: getOnChainVerifyingContract(),
  };

  return signedPost('/v1/withdraw_request', body, auth);
}

export async function manualConvert(payload: {
  converted_asset: string;
  amount: number;
  slippage: number;
}, auth: AuthContext, accountId?: string): Promise<unknown> {
  return signedPost('/v1/asset/manual_convert', payload, auth, accountId);
}

// ── Ed25519 Signed Endpoints ──

export async function getSubAccounts(auth: AuthContext): Promise<{ main_account_id: string; rows: { sub_account_id: string; description: string }[] }> {
  return signedGet('/v1/client/sub_account', auth);
}

export async function getAggregatePositions(auth: AuthContext): Promise<{
  rows: { symbol: string; margin_mode: MarginMode; position_qty: number; cost_position: number; last_sum_unitary_funding: number; pending_long_qty: number; pending_short_qty: number; settle_price: number; average_open_price: number; unsettled_pnl: number; mark_price: number; est_liq_price: number; timestamp: number; imr: number; mmr: number; IMR_withdraw_orders: number; MMR_with_orders: number; pnl_24_h: number; fee_24_h: number; account_id: string }[]
}> {
  return signedGet('/v1/client/aggregate/positions', auth);
}

export async function getPositions(auth: AuthContext, accountId: string): Promise<{
  rows: { symbol: string; margin_mode: MarginMode; position_qty: number; cost_position: number; last_sum_unitary_funding: number; pending_long_qty: number; pending_short_qty: number; settle_price: number; average_open_price: number; unsettled_pnl: number; mark_price: number; est_liq_price: number; timestamp: number; imr: number; mmr: number; IMR_withdraw_orders: number; MMR_with_orders: number; pnl_24_h: number; fee_24_h: number }[]
}> {
  return signedGet('/v1/positions', auth, undefined, accountId);
}

export async function getHolding(auth: AuthContext, accountId?: string): Promise<{
  rows: { token: string; holding: number; frozen: number; updated_time: number }[]
}> {
  const result = await signedGet<{
    holding: { token: string; holding: number; frozen: number; updated_time: number }[]
  }>('/v1/client/holding', auth, undefined, accountId);
  return { rows: result.holding ?? [] };
}

export async function getAggregateHolding(auth: AuthContext): Promise<{
  rows: { token: string; holding: number; frozen: number; interest: number; outstanding_holding: number; fee_24_h: number; settle_pnl: number; updated_time: number; account_id: string }[]
}> {
  return signedGet('/v1/client/aggregate/holding', auth);
}

export async function cancelAllOrders(auth: AuthContext, symbol?: string, accountId?: string): Promise<unknown> {
  const params = symbol ? { symbol } : undefined;
  return signedDelete('/v1/orders', auth, params, accountId);
}

export async function cancelAllAlgoOrders(auth: AuthContext, accountId?: string): Promise<unknown> {
  return signedDelete('/v1/algo/orders', auth, undefined, accountId);
}

export async function getOpenOrders(auth: AuthContext, accountId?: string): Promise<{ rows: { order_id: number; account_id?: string; symbol: string; side: string; type: string; quantity: number; price: number }[] }> {
  return signedGet('/v1/orders', auth, { status: 'INCOMPLETE' }, accountId);
}

export async function getOpenAlgoOrders(auth: AuthContext, accountId?: string): Promise<{ rows: { algo_order_id: number; account_id?: string; symbol: string; side: string; type: string; quantity: number; trigger_price: number }[] }> {
  return signedGet('/v1/algo/orders', auth, { status: 'INCOMPLETE' }, accountId);
}

export async function placeOrder(order: {
  symbol: string;
  order_type: string;
  side: string;
  order_quantity: number;
  reduce_only: boolean;
  margin_mode?: MarginMode;
}, auth: AuthContext, accountId?: string): Promise<unknown> {
  return signedPost('/v1/order', order, auth, accountId);
}

export async function getSettleNonce(auth: AuthContext, accountId?: string): Promise<{ settle_nonce: number }> {
  return signedGet('/v1/settle_nonce', auth, undefined, accountId);
}

export async function subAccountSettlePnl(nonce: number, accountId: string, auth: AuthContext): Promise<unknown> {
  return signedPost('/v1/sub_account_settle_pnl', { settle_nonce: nonce, orderly_account_id: accountId }, auth, accountId);
}

export async function getTransferNonce(auth: AuthContext): Promise<{ transfer_nonce: number }> {
  return signedGet('/v1/transfer_nonce', auth);
}

export async function internalTransfer(payload: {
  token: string;
  amount: number | string;
  from_account_id: string;
  to_account_id: string;
}, auth: AuthContext): Promise<unknown> {
  const amount = typeof payload.amount === 'number' ? payload.amount : Number(payload.amount);
  const body = {
    token: payload.token,
    receiver_list: [
      {
        account_id: payload.to_account_id,
        amount,
      },
    ],
  };

  return signedPost('/v1/internal_transfer', body, auth, payload.from_account_id);
}

export async function getWithdrawNonce(auth: AuthContext): Promise<{ withdraw_nonce: number }> {
  return signedGet('/v1/withdraw_nonce', auth);
}
